/**
 * Tracker de combate (o "Tracker de Combate" do módulo embutido do original, uma
 * tabela da sala): ordem de iniciativa, rodada, turno, lado (amigável/neutro/hostil),
 * visibilidade, anotações e efeitos com duração em rodadas.
 *
 * Documento NDB do item `dock` da sala:
 * - raiz: `round` (rodada atual) e `turn` (id do ator da vez, '' antes de começar);
 * - filhos `actor`: `name`, `init` (número; '' = sem iniciativa), `roll` (expressão da
 *   iniciativa), `side` friend | neutral | foe | '', `vis` public | protected | private,
 *   `order` (ordem de entrada, para desempatar) e, ligado a um token da cena, `sceneId` e
 *   `tokenId` (BRAZ-24);
 * - cada ator tem um filho `info` com `notes` e filhos `effect` (`name`, `rounds`).
 *
 * Visibilidade pelas permissões do NDB (o servidor não entrega o que alguém não lê):
 * `private` nega leitura do ator a todos (só mestres veem); `protected` nega leitura
 * do `info` (todos veem o ator, não as anotações nem os efeitos). O ator ligado a um token
 * invisível só é lido por quem vê o token: essa leitura é o servidor que grava
 * (`packages/shared/src/combatLink.ts`).
 */
import { ACTOR_SCENE_ATTR, ACTOR_TOKEN_ATTR, COMBAT_TRACKER, NDB_GROUPS, actorReadPerms, type TokenReadState } from '../../shared.js';
import type { AttrValue, NodeTree, Op, TreeNode } from '../../ndb.js';

/** Tipo de dado da tabela embutida (não vem de plugin). */
export { COMBAT_TRACKER };

export type Side = 'friend' | 'neutral' | 'foe' | '';
export type Visibility = 'public' | 'protected' | 'private';
export type Effect = { id: string; name: string; rounds: number };
export type Actor = {
  id: string;
  name: string;
  init: number | null;
  roll: string;
  side: Side;
  vis: Visibility;
  order: number;
  /** null quando o `info` não é visível para quem lê (ator protegido) */
  infoId: string | null;
  notes: string;
  effects: Effect[];
  /** a cena e o token a que o ator está ligado (null: sem ligação) */
  sceneId: string | null;
  tokenId: string | null;
  /** os jogadores, em geral, não leem o ator (privado, ou ligado a um token invisível) */
  concealed: boolean;
  /** ligado a um token invisível: só quem vê o token vê o ator */
  invisible: boolean;
};
export type Tracker = { round: number; turn: string | null; actors: Actor[] };

const SIDES: readonly Side[] = ['friend', 'neutral', 'foe', ''];
const VIS: readonly Visibility[] = ['public', 'protected', 'private'];

const num = (v: AttrValue | undefined): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : null;
};
const str = (v: AttrValue | undefined) => (typeof v === 'string' ? v : v === undefined ? '' : String(v));

function readActor(node: TreeNode, index: number): Actor {
  const a = node.attrs;
  const info = node.children.find((c) => c.name === 'info') ?? null;
  const side = str(a.get('side')) as Side;
  const vis = str(a.get('vis')) as Visibility;
  const sceneId = str(a.get(ACTOR_SCENE_ATTR)) || null;
  const tokenId = str(a.get(ACTOR_TOKEN_ATTR)) || null;
  const concealed = node.perms.some((p) => p.kind === 'group' && p.id === NDB_GROUPS.everyone && p.permission === 'read' && p.allowance === 'deny');
  return {
    id: node.id,
    name: str(a.get('name')),
    init: num(a.get('init')),
    roll: str(a.get('roll')),
    side: SIDES.includes(side) ? side : '',
    vis: VIS.includes(vis) ? vis : 'public',
    order: num(a.get('order')) ?? index,
    infoId: info?.id ?? null,
    notes: info ? str(info.attrs.get('notes')) : '',
    effects: (info?.children ?? [])
      .filter((c) => c.name === 'effect')
      .map((e) => ({ id: e.id, name: str(e.attrs.get('name')), rounds: Math.max(0, Math.trunc(num(e.attrs.get('rounds')) ?? 0)) })),
    sceneId: sceneId && tokenId ? sceneId : null,
    tokenId: sceneId && tokenId ? tokenId : null,
    concealed,
    invisible: !!sceneId && !!tokenId && concealed && vis !== 'private',
  };
}

/** Ordem de combate: maior iniciativa primeiro; sem iniciativa no fim; empate pela entrada. */
export function sortActors(actors: Actor[]): Actor[] {
  return [...actors].sort((p, q) => {
    if (p.init === null && q.init !== null) return 1;
    if (q.init === null && p.init !== null) return -1;
    return (q.init ?? 0) - (p.init ?? 0) || p.order - q.order;
  });
}

export function readTracker(tree: NodeTree): Tracker {
  const root = tree.root;
  const actors = root.children.filter((c) => c.name === 'actor').map(readActor);
  const turn = str(root.attrs.get('turn')) || null;
  return { round: Math.max(1, Math.trunc(num(root.attrs.get('round')) ?? 1)), turn: actors.some((a) => a.id === turn) ? turn : null, actors: sortActors(actors) };
}

export function setOps(node: string, attrs: Record<string, AttrValue | null>): Op[] {
  return Object.entries(attrs).map(([name, value]) => ({ t: 'set', node, name, value }));
}

const denyRead = (node: string, on: boolean): Op => ({ t: 'perm', node, kind: 'group', id: NDB_GROUPS.everyone, permission: 'read', allowance: on ? 'deny' : null });

/**
 * Permissões da visibilidade: ator privado e `info` protegido negam leitura a todos. Do ator ligado a
 * um token (`linked`), quem acerta a leitura é o servidor, que segue o token: aqui, só o privado.
 */
export function visibilityOps(actorId: string, infoId: string, vis: Visibility, linked = false): Op[] {
  return [...(linked && vis !== 'private' ? [] : [denyRead(actorId, vis === 'private')]), denyRead(infoId, vis === 'protected'), ...setOps(actorId, { vis })];
}

/** O token a que o ator novo fica ligado e o que pesa em quem o vê (BRAZ-24). */
export type ActorLink = { sceneId: string; tokenId: string; token: TokenReadState };

export type NewActor = { name: string; side?: Side; vis?: Visibility; roll?: string; init?: number | null; link?: ActorLink };

export function addActorOps(rootId: string, id: string, infoId: string, actor: NewActor, order: number): Op[] {
  const vis = actor.vis ?? 'public';
  const { link } = actor;
  return [
    { t: 'add', parent: rootId, id, name: 'actor' },
    ...setOps(id, { name: actor.name, side: actor.side ?? '', roll: actor.roll ?? '', init: actor.init ?? '', order }),
    { t: 'add', parent: id, id: infoId, name: 'info' },
    // ligado a um token invisível, o ator já nasce só para quem vê o token (o nome não vaza)
    ...(link
      ? [
          ...setOps(id, { [ACTOR_SCENE_ATTR]: link.sceneId, [ACTOR_TOKEN_ATTR]: link.tokenId }),
          ...actorReadPerms(vis, link.token, []).map((p): Op => ({ t: 'perm', node: id, kind: p.kind, id: p.id, permission: 'read', allowance: p.allowance })),
          ...visibilityOps(id, infoId, vis, true),
        ]
      : visibilityOps(id, infoId, vis)),
  ];
}

/** Liga o ator a um token da cena (null desliga); a leitura dele o servidor acerta. */
export const linkActorOps = (actorId: string, link: { sceneId: string; tokenId: string } | null): Op[] =>
  setOps(actorId, { [ACTOR_SCENE_ATTR]: link?.sceneId ?? null, [ACTOR_TOKEN_ATTR]: link?.tokenId ?? null });

export function addEffectOps(infoId: string, id: string, name: string, rounds: number): Op[] {
  return [{ t: 'add', parent: infoId, id, name: 'effect' }, ...setOps(id, { name, rounds: Math.max(1, Math.trunc(rounds)) })];
}

export type TurnChange = { ops: Op[]; actor: Actor | null; ended: { effect: Effect; actor: Actor }[]; round: number };

/**
 * A vez sai da posição `index` da ordem para a seguinte; depois do último, volta ao primeiro, a rodada
 * avança e os efeitos de `effectsOf` perdem uma rodada (os que chegam a zero acabam).
 */
function advance(tracker: Tracker, rootId: string, index: number, effectsOf: readonly Actor[]): TurnChange {
  const order = tracker.actors;
  const wraps = index === order.length - 1;
  const next = order[(index + 1) % order.length]!;
  const round = wraps ? tracker.round + 1 : tracker.round;
  const ops: Op[] = setOps(rootId, { turn: next.id, round });
  const ended: { effect: Effect; actor: Actor }[] = [];
  if (wraps) {
    for (const actor of effectsOf) {
      for (const effect of actor.effects) {
        if (effect.rounds <= 1) {
          ops.push({ t: 'del', node: effect.id });
          ended.push({ effect, actor });
        } else ops.push(...setOps(effect.id, { rounds: effect.rounds - 1 }));
      }
    }
  }
  return { ops, actor: next, ended, round };
}

/**
 * Passa o turno: o próximo na ordem; depois do último, volta ao primeiro e a rodada
 * avança, e os efeitos perdem uma rodada (os que chegam a zero acabam).
 * Devolve as operações, quem é a vez e os efeitos que acabaram.
 */
export function nextTurn(tracker: Tracker, rootId: string): TurnChange {
  if (!tracker.actors.length) return { ops: [], actor: null, ended: [], round: tracker.round };
  const index = tracker.turn ? tracker.actors.findIndex((a) => a.id === tracker.turn) : -1;
  return advance(tracker, rootId, index, tracker.actors);
}

/**
 * Volta o turno (BRAZ-29): o anterior na ordem; antes do primeiro (ou antes de começar), o último, e a
 * rodada volta uma (a 1 é a menor que o tracker guarda). Os efeitos que a passagem de rodada gastou
 * ganham a rodada de volta; os que acabaram já foram apagados e não voltam.
 */
export function prevTurn(tracker: Tracker, rootId: string): TurnChange {
  const order = tracker.actors;
  if (!order.length) return { ops: [], actor: null, ended: [], round: tracker.round };
  const index = tracker.turn ? order.findIndex((a) => a.id === tracker.turn) : -1;
  const wraps = index <= 0;
  const prev = order[wraps ? order.length - 1 : index - 1]!;
  const round = wraps ? Math.max(1, tracker.round - 1) : tracker.round;
  const ops: Op[] = setOps(rootId, { turn: prev.id, round });
  if (wraps && round < tracker.round) {
    for (const actor of order) for (const effect of actor.effects) ops.push(...setOps(effect.id, { rounds: effect.rounds + 1 }));
  }
  return { ops, actor: prev, ended: [], round };
}

/**
 * Quem está na vez sai do lugar dele na ordem: foi removido (`removed`) ou a iniciativa mudou e ele
 * trocou de posição (adiou, ou foi para antes). A vez passa a quem vinha depois dele na ordem de antes,
 * como no "Próximo" (o removido não gasta efeitos). Sem isso, a vez apagada recomeçava a rodada pelo
 * primeiro, e a que seguia da posição nova pulava quem estava no meio (ou repetia quem já tinha agido).
 * Null se ele não está na vez.
 */
export function turnLeaves(tracker: Tracker, rootId: string, actorId: string, removed: boolean): TurnChange | null {
  if (!tracker.turn || tracker.turn !== actorId) return null;
  const index = tracker.actors.findIndex((a) => a.id === actorId);
  if (index < 0) return null;
  const rest = removed ? tracker.actors.filter((a) => a.id !== actorId) : tracker.actors;
  // era o único: não sobra ninguém para a vez
  if (rest.length === 0 || (rest.length === 1 && !removed)) return removed ? { ops: setOps(rootId, { turn: '' }), actor: null, ended: [], round: tracker.round } : null;
  return advance(tracker, rootId, index, rest);
}

/** Recomeça: rodada 1, ninguém na vez (os atores ficam). */
export function resetOps(rootId: string): Op[] {
  return setOps(rootId, { round: 1, turn: '' });
}
